import React, { FunctionComponent, useCallback, useEffect, useState } from 'react'
import { FieldRenderProps } from 'react-final-form'
import { Platform, ViewStyle } from 'react-native'

import { isNil, isNumber } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { SliderField, STEP_INDICATOR_MARGIN_OFFSET } from './SliderField'
import sliderThumbGreen from '../../assets/sliderThumbGreen.png'
import sliderThumbLightGreen from '../../assets/sliderThumbLightGreen.png'
import sliderThumbPink from '../../assets/sliderThumbPink.png'
import sliderThumbPurple from '../../assets/sliderThumbPurple.png'
import sliderThumbYellow from '../../assets/sliderThumbYellow.png'
import { IS_WEB } from '../../constants'
import { ThemeType } from '../../utils/themes/ThemeProvider'

const MobileSliderBar = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  position: 'absolute',
  borderWidth: '1px',
  borderColor: theme.colors.borderDefault,
  height: theme.spacing['40px'],
  width: '100%',
  borderRadius: '100px',
  backgroundColor: theme.colors.backgroundPrimary,
  ...(Platform.OS === 'ios' && { top: theme.spacing['8px'] }),
}))

export const MulticolorSliderFieldRFF: FunctionComponent<FieldRenderProps<number | undefined>> = ({
  input: { value, onChange, name, onFocus, onBlur },
  meta: { touched, error, active },
  label,
  minimum,
  maximum,
  minLabel,
  maxLabel,
  initValue,
  accessibilityLabelledBy,
  rangeContainerStyle,
  rangeLabelStyle,
  disableThumbAnimationOnDrag,
}) => {
  const {
    breakpoints: { isMobileSized },
    colors,
  } = useTheme() as ThemeType

  const multicolorSliderComponentColors = colors.components.multicolorSliderField

  useEffect(() => {
    if (!isNil(initValue) && isNil(value)) {
      onChange(initValue)
    }
  }, [initValue, onChange, value])

  const currValue = isNumber(value) ? value : initValue || 0

  const [multicolorThumbPositions, setMulticolorThumbPositions] = useState<number[]>([])

  const [thumbImagePressed, setThumbImagePressed] = useState(false)
  const handleOnThumbImagePressed = (thumbPressed: boolean) => {
    setThumbImagePressed(thumbPressed)
  }

  const handleUseLayoutEffect = useCallback(
    (numberOfSteps: number, sliderWidth: number) => {
      const thumbAdjustment = isMobileSized ? 48 : 40
      const stepMarkerOffset = isMobileSized ? (thumbImagePressed ? 8 : 10) : thumbImagePressed ? 20 : 22
      const newThumbPositions = [...Array(numberOfSteps + 1).keys()].map((_, index) => {
        const valueInPixels = ((sliderWidth - thumbAdjustment * 2) / numberOfSteps) * index
        return valueInPixels + (isMobileSized ? stepMarkerOffset + 7 * index : stepMarkerOffset - index)
      })
      setMulticolorThumbPositions(newThumbPositions)
    },
    [isMobileSized, thumbImagePressed],
  )

  const TRACK_HEIGHT = isMobileSized ? 40 : 32
  const THUMB_SIZE = isMobileSized ? (thumbImagePressed ? 52 : 48) : thumbImagePressed ? 44 : 40
  const STEP_MARKER_SIZE = 20

  // these colors aren't used anywhere else in the app, so not getting them from color tokens
  // DO NOT MOVE ASSETS TO CDN, Slider Library does not support remote images
  const colorMapping = [
    { backgroundColor: '#C7A4C2', thumbImage: sliderThumbPurple },
    { backgroundColor: '#FFC8B9', thumbImage: sliderThumbPink },
    { backgroundColor: '#FFDE74', thumbImage: sliderThumbYellow },
    { backgroundColor: '#E3F0B0', thumbImage: sliderThumbLightGreen },
    { backgroundColor: '#C1E0B7', thumbImage: sliderThumbGreen },
  ]

  return (
    <>
      {!IS_WEB && <MobileSliderBar />}
      <SliderField
        label={label}
        value={currValue}
        onValueChange={onChange}
        minimumValue={parseInt(minimum, 10)}
        maximumValue={parseInt(maximum, 10)}
        minLabel={minLabel}
        maxLabel={maxLabel}
        error={touched && error}
        name={name}
        onFocus={onFocus}
        onBlur={onBlur}
        active={active}
        accessibilityLabelledBy={accessibilityLabelledBy}
        showStepMarkers
        onUseLayoutEffect={handleUseLayoutEffect}
        onThumbImagePressed={handleOnThumbImagePressed}
        rangeContainerStyle={rangeContainerStyle}
        rangeLabelStyle={rangeLabelStyle}
        disableThumbAnimationOnDrag={disableThumbAnimationOnDrag}
        customStyles={{
          componentColors: multicolorSliderComponentColors,
          trackHeight: TRACK_HEIGHT,
          labelTextStyle: { color: colors.textSecondary },
          showFirstAndLast: true,
          stepIndicatorContainerStyle: {
            marginHorizontal: isMobileSized ? 24 + STEP_INDICATOR_MARGIN_OFFSET : 32 + STEP_INDICATOR_MARGIN_OFFSET,
          },
          minTrackStyle: {
            borderColor: colors.borderDefault,
            borderWidth: 1,
            borderRightWidth: 0,
            top: 0,
            ...(value === minimum && {
              display: 'none',
            }),
            ...(value === maximum && {
              borderRightWidth: 1,
              borderTopRightRadius: TRACK_HEIGHT / 2,
              borderBottomRightRadius: TRACK_HEIGHT / 2,
              marginRight: 0,
            }),
          },
          maxTrackStyle: {
            borderColor: colors.borderDefault,
            borderWidth: 1,
            borderLeftWidth: 0,
            top: 0,
            ...(value === maximum && {
              display: 'none',
            }),
            ...(value === minimum && {
              borderLeftWidth: 1,
              borderTopLeftRadius: TRACK_HEIGHT / 2,
              borderBottomLeftRadius: TRACK_HEIGHT / 2,
              marginLeft: 0,
            }),
          },
          stepThumbStyle: {
            top: Platform.OS === 'android' ? 12 : 10,
          },
          ...(isNumber(currValue) && {
            ...(IS_WEB
              ? {
                  thumbStyle: {
                    backgroundColor: colorMapping[currValue].backgroundColor,
                    left: multicolorThumbPositions[currValue],
                    boxShadow: `0px 2px 16px 0px ${multicolorSliderComponentColors.thumb.shadow.fill}`,
                    position: 'absolute',
                    border: `4px solid ${multicolorSliderComponentColors.thumb.border}`,
                    width: THUMB_SIZE,
                    height: THUMB_SIZE,
                  } as ViewStyle,
                }
              : { thumbImage: colorMapping[currValue].thumbImage as unknown as string }),
            stepMarkerStyles: colorMapping.map((item) => {
              return {
                width: STEP_MARKER_SIZE,
                height: STEP_MARKER_SIZE,
                top: IS_WEB ? 6 : 0,
                borderRadius: STEP_MARKER_SIZE,
                backgroundColor: item.backgroundColor,
              }
            }),
          }),
        }}
      />
    </>
  )
}
